import { useCallback, useEffect, useRef, useState } from "react";
import "./bulk-assign-console.scss";
import { Button, Chip, CircularProgress, TextField, Typography } from "@material-ui/core";
import { api } from "../../services/apiService";
import {
  assignedToYouLabel,
  assigneeLabel,
  buildBulkCriteria,
  bulkAssignOutcome,
  bulkConfirmEnabled,
  BULK_ASSIGN_STRATEGIES,
  normalizeRoster,
  railVisible,
  searchRoster,
} from "./review-queue-logic";

// QIR B5 (§6.2 / §6.4-L2): the tutor "Assigned to you (n)" rail variant, built INERT behind
// the server visibility mode. Renders ONLY under ASSIGNED_ONLY; under the shipped ALL_READY
// default it renders nothing, so today's tutor rail is byte-unchanged (the DARK ship). The
// count is presentational — the server owns the actual filtering at its choke point (I2).
export function AssignedToYouRail({ mode, count }) {
  if (!railVisible(mode)) return null;
  return (
    <div className="rq-assigned-rail">
      <Typography variant="subtitle2" className="rq-assigned-rail-title">
        {assignedToYouLabel(count)}
      </Typography>
      {(!count || count <= 0) && (
        <Typography variant="caption" className="rq-assigned-rail-empty">
          No questions assigned to you yet.
        </Typography>
      )}
    </div>
  );
}

// QIR B5 (§6.3): admin-only bulk-assign console — criteria pickers + a live preview count
// (blast radius) + reviewer multi-select + strategy, with Confirm PREVIEW-GATED (I1 UX
// mirror: the server rejects a commit without a matched preview only implicitly, but the UI
// never enables Confirm until a preview has resolved). Mounted ONLY for admins, so the tutor
// roster never leaks (AD-4). The ACL is server-enforced; this is a control surface.
export function BulkAssignConsole({ isAdmin, type = "IMAGE_CANDIDATE", examType = "" }) {
  const [open, setOpen] = useState(false);
  const [fields, setFields] = useState({
    examType, organSystem: "", discipline: "", visualKind: "", precisionClass: "", state: "", subjectKeys: "",
  });
  const [reviewerIds, setReviewerIds] = useState([]);
  const [strategy, setStrategy] = useState("ALL_TO_EACH");
  const [cap, setCap] = useState("");
  const [batchLabel, setBatchLabel] = useState("");

  const [roster, setRoster] = useState([]);
  const [rosterState, setRosterState] = useState("idle");
  const [query, setQuery] = useState("");

  const [preview, setPreview] = useState(null); // { matchedCount, perReviewer } — cleared on ANY edit
  const [result, setResult] = useState(null); // commit result { created, alreadyAssigned, ... }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  // Any change to the selection INVALIDATES a prior preview so Confirm re-locks — Confirm can
  // never fire against a stale blast radius (preview-before-commit).
  const invalidate = () => { setPreview(null); setResult(null); setError(null); };
  const setField = (k, v) => { setFields((f) => ({ ...f, [k]: v })); invalidate(); };
  const toggleReviewer = (id) => {
    setReviewerIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    invalidate();
  };
  const pickStrategy = (k) => { setStrategy(k); invalidate(); };

  const loadRoster = useCallback(() => {
    setRosterState("loading");
    api.getUsersByRole("TUTOR")
      .then((res) => {
        if (!mountedRef.current) return;
        if (!res || (typeof res === "object" && !Array.isArray(res) && res.status)) {
          setRoster([]); setRosterState("error"); return;
        }
        setRoster(normalizeRoster(res)); setRosterState("ok");
      })
      .catch(() => { if (mountedRef.current) { setRoster([]); setRosterState("error"); } });
  }, []);

  const openConsole = () => { setOpen(true); if (rosterState === "idle" || rosterState === "error") loadRoster(); };

  const body = () => ({
    criteria: buildBulkCriteria({ ...fields, reviewType: type }),
    reviewerIds,
    strategy,
    ...(String(cap).trim() !== "" ? { cap: Number(cap) } : {}),
    ...(batchLabel.trim() !== "" ? { batchLabel: batchLabel.trim() } : {}),
  });

  const doPreview = () => {
    setBusy(true); setError(null); setResult(null);
    api.bulkAssignReviewers(body(), { preview: true })
      .then((res) => {
        if (!mountedRef.current) return;
        const outcome = bulkAssignOutcome(res);
        if (outcome === "ok") setPreview(res);
        else { setPreview(null); setError(outcome === "dark" ? "Bulk assign isn’t enabled on this server yet." : "Couldn’t preview — check the criteria (or it exceeds the cap)."); }
      })
      .catch(() => mountedRef.current && setError("Couldn’t preview — please retry."))
      .finally(() => mountedRef.current && setBusy(false));
  };

  const doConfirm = () => {
    if (!bulkConfirmEnabled({ preview, reviewerIds, busy })) return;
    setBusy(true); setError(null);
    api.bulkAssignReviewers(body(), { preview: false })
      .then((res) => {
        if (!mountedRef.current) return;
        const outcome = bulkAssignOutcome(res);
        if (outcome === "ok") { setResult(res); setPreview(null); }
        else setError(outcome === "dark" ? "Bulk assign isn’t enabled on this server yet." : "Couldn’t assign — please retry.");
      })
      .catch(() => mountedRef.current && setError("Couldn’t assign — please retry."))
      .finally(() => mountedRef.current && setBusy(false));
  };

  if (!isAdmin) return null;

  if (!open) {
    return (
      <div className="rq-bulk-toggle">
        <Button size="small" variant="text" className="rq-bulk-open" onClick={openConsole}>
          Bulk assign reviewers
        </Button>
      </div>
    );
  }

  const assignable = searchRoster(roster, query);
  const confirmReady = bulkConfirmEnabled({ preview, reviewerIds, busy });

  const textField = (key, label) => (
    <TextField
      className={`rq-bulk-field rq-bulk-${key}`}
      label={label}
      variant="outlined"
      size="small"
      value={fields[key]}
      onChange={(e) => setField(key, e.target.value)}
    />
  );

  return (
    <div className="rq-bulk">
      <div className="rq-bulk-head">
        <Typography variant="subtitle2" className="rq-bulk-title">Bulk assign by criteria</Typography>
        <Button size="small" variant="text" className="rq-bulk-close" onClick={() => setOpen(false)}>Close</Button>
      </div>

      <Typography variant="caption" className="rq-bulk-sub">Criteria</Typography>
      <div className="rq-bulk-criteria">
        {textField("examType", "Exam type")}
        {textField("organSystem", "Organ system")}
        {textField("discipline", "Discipline")}
        {textField("visualKind", "Visual kind")}
        {textField("precisionClass", "Precision class")}
        {textField("state", "State (READY | IN_REVIEW)")}
        <TextField
          className="rq-bulk-field rq-bulk-subjectKeys"
          label="Explicit subject keys (optional — wins over criteria)"
          variant="outlined" size="small" fullWidth multiline
          value={fields.subjectKeys}
          onChange={(e) => setField("subjectKeys", e.target.value)}
        />
      </div>

      <Typography variant="caption" className="rq-bulk-sub">Reviewers</Typography>
      {rosterState === "loading" && <div className="rq-bulk-roster-loading"><CircularProgress size={18} /></div>}
      {rosterState === "error" && (
        <div className="rq-bulk-roster-error">
          <Typography variant="caption" color="error">Couldn’t load the tutor list.</Typography>
          <Button size="small" variant="outlined" className="rq-bulk-roster-retry" onClick={loadRoster}>Retry</Button>
        </div>
      )}
      {rosterState === "ok" && (
        <>
          <TextField
            className="rq-bulk-search" placeholder="Search tutors by name or email"
            variant="outlined" size="small" fullWidth
            value={query} onChange={(e) => setQuery(e.target.value)}
          />
          <ul className="rq-bulk-roster">
            {assignable.map((u) => (
              <li key={u.id} className="rq-bulk-roster-row">
                <label>
                  <input
                    type="checkbox"
                    className="rq-bulk-reviewer-check"
                    checked={reviewerIds.includes(u.id)}
                    onChange={() => toggleReviewer(u.id)}
                  />
                  <span className="rq-bulk-reviewer-name">{assigneeLabel(u.id, roster)}</span>
                </label>
              </li>
            ))}
          </ul>
        </>
      )}
      <Typography variant="caption" className="rq-bulk-selected">
        {reviewerIds.length} reviewer{reviewerIds.length === 1 ? "" : "s"} selected
      </Typography>

      <Typography variant="caption" className="rq-bulk-sub">Strategy</Typography>
      <div className="rq-bulk-strategies">
        {BULK_ASSIGN_STRATEGIES.map((s) => (
          <Chip
            key={s.key}
            className={`rq-bulk-strategy rq-bulk-strategy-${s.key}`}
            label={s.label}
            color={strategy === s.key ? "primary" : "default"}
            variant={strategy === s.key ? "default" : "outlined"}
            onClick={() => pickStrategy(s.key)}
          />
        ))}
      </div>

      <div className="rq-bulk-opts">
        <TextField className="rq-bulk-cap" label="Cap (optional)" type="number" variant="outlined" size="small"
          value={cap} onChange={(e) => { setCap(e.target.value); invalidate(); }} />
        <TextField className="rq-bulk-batch" label="Batch label (optional)" variant="outlined" size="small"
          value={batchLabel} onChange={(e) => { setBatchLabel(e.target.value); invalidate(); }} />
      </div>

      <div className="rq-bulk-actions">
        <Button className="rq-bulk-preview-btn" variant="outlined" size="small" disabled={busy} onClick={doPreview}>
          Preview
        </Button>
        <Button className="rq-bulk-confirm-btn" variant="contained" color="primary" size="small"
          disabled={!confirmReady} onClick={doConfirm}>
          Confirm assign
        </Button>
      </div>

      {preview && (
        <div className="rq-bulk-blast">
          <Typography variant="body2" className="rq-bulk-blast-count">
            {preview.matchedCount} question{preview.matchedCount === 1 ? "" : "s"} match — will assign to {reviewerIds.length} reviewer{reviewerIds.length === 1 ? "" : "s"}.
          </Typography>
        </div>
      )}
      {result && (
        <div className="rq-bulk-result">
          <Typography variant="body2" className="rq-bulk-result-line">
            Assigned: {result.created} created, {result.alreadyAssigned} already assigned across {result.matchedCount} question{result.matchedCount === 1 ? "" : "s"}.
          </Typography>
        </div>
      )}
      {error && <Typography variant="caption" color="error" className="rq-bulk-error">{error}</Typography>}
    </div>
  );
}

export default BulkAssignConsole;
